// Session learning objectives.
//
// Structured data, not prose. Two reasons: the screen needs to be glanceable, and the
// objectives are the spine the assessments hang off — the two domains the counterbalance
// is built on (counterbalance.js: a1/a2 concurrency, b1/b2 error handling). Keeping them
// as data means quiz items and tasks can be tagged against the same list.
//
// PITCH: plain English, no library vocabulary. A previous draft named the constructs
// (`start_soon`, "nursery", "async functions") so each objective mapped tightly onto a
// quiz item — but participants are SCREENED to be new to Trio, so on this screen those
// words are noise. An objective you can't parse isn't an objective, and the mapping is
// preserved anyway in `assesses` below, which is what scoring uses.
//
// Rule for edits: if a sentence would confuse someone who has never written async code,
// it is wrong for this screen no matter how precise it is.
//
// `assesses` records the quiz items each objective covers, from graders/quiz_final_KEYS.md.
// It isn't rendered — it's here so the mapping lives with the objectives rather than in
// someone's notes, and so scoring can group by objective later.
//
// NOT LISTED, deliberately: cancel scopes (Q-I, 2 pts) and races on shared state
// (Q-N, 3 pts). Both are on the quiz; neither is taught anywhere in the session —
// move_on_after arrives pre-written in a1_starter and races appear in no task. That's
// 5 of the quiz's 27 points with no matching objective.
export const SESSION_OBJECTIVES = [
  {
    domain: 'concurrency',
    // The domain's own name, not a paraphrase. counterbalance.js labels these
    // "Trio's concurrency features" / "Trio's error-handling features", and
    // MID_SESSION_QUESTIONS asks participants to rate their understanding in exactly
    // those words. A paraphrase here would leave them matching up two vocabularies.
    title: 'Concurrency',
    items: [
      { text: 'Make a program do several things at once instead of one after another', assesses: ['Q-A', 'Q-H'] },
      { text: 'Have a program wait for those things to finish before it moves on', assesses: ['Q-B', 'Q-C', 'Q-K'] },
      { text: 'Start a job without stopping to wait for its answer', assesses: ['Q-D'] },
      { text: 'Look at code and say what order it prints in, and how long it takes', assesses: ['Q-E', 'Q-F', 'Q-G', 'Q-J'] }
    ]
  },
  {
    domain: 'error_handling',
    title: 'Error handling',
    items: [
      { text: 'Stop one job that goes wrong from killing all the others', assesses: ['Q-L'] },
      { text: 'Collect the answers when jobs finish in an unpredictable order', assesses: ['Q-M'] }
    ]
  }
]

export const OBJECTIVES_LEAD = 'What you should be able to do by the end of this session.'

// Flat 1..N view of the objectives, carrying the number shown on the objectives screen.
// The numbering is continuous ACROSS groups there, so it cannot be derived from a
// group's own index — it has to be computed once, here, or the same objective ends up
// with two different numbers in two places.
export const NUMBERED_OBJECTIVES = SESSION_OBJECTIVES.flatMap(g => g.items.map(it => ({ ...it, domain: g.domain, group: g.title })))
  .map((it, i) => ({ ...it, n: i + 1 }))

// The objectives a given task practises, by the task's own `domain` field
// (tasks.js: a1/a2 concurrency, b1/b2 error_handling, warmup screening).
//
// The objectives screen is shown once and then gone, which left participants with no
// way back to it — and no signal about which objective any given task was serving.
// Returning them per task lets the task doc carry its own objectives inline.
export function objectivesForDomain (domain) {
  return NUMBERED_OBJECTIVES.filter(o => o.domain === domain)
}
